import { useState, useEffect, useMemo, createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { useInViewport, useLocalStorage } from '@mantine/hooks';
import { useLocation } from 'react-router-dom';
import { useMediaQuery, useTheme, type SxProps } from '@mui/material';
import { AxiosError } from 'axios';

import type { TWidget, TWidgetData } from '../../utility/types/dataProvider';
import { useIsCustomDashboard } from './hooks/useIsCustomDashboard';
import { useDashboardContext } from 'screens/dashboard/Dashboard';
import { rssDisplayTypes, independentSystemWidgetIds } from 'screens/dashboard/constants';
import { getIsIndependentSystemWidget } from 'screens/dashboard/utils';
import {
  getRefreshIndicator,
  getMaskedWidgetData,
  getIsWidgetFetchEnabled,
  normalizeDashboardFiltersQuery,
  normalizeModuleParameters,
  getChartConfig,
  getInitDashboardOrWidgetFiltersFromModuleDynaFields,
  getInitDashboardOrWidgetFiltersFromWidgetParameters,
  getIsSubscriptionPurchaseEventsWidget,
} from './shared/utils';
import { STALE_TIME } from './shared/constants';
import { getWidgetAppValue } from './hooks/useDashboardWidgetHelpers';
import KPI from './widgets/KPIs/KPI';
import RssLayout from './widgets/Rss/RssLayout';
import WidgetCard from './WidgetCard';
import type { TWidgetContext, TWidgetFilters, TLoadingStatusPill } from './types/widget';
import { parseKpiData } from './hooks/useParseKpiData';

// Widget Context.
export const WidgetContext = createContext<TWidgetContext>({
  data: {} as TWidgetData,
  isError: false,
  pagination: undefined,
  isLoading: false,
  isFetching: false,
  widget: {} as TWidget,
  isMasked: false,
  chartType: null,
  config: undefined,
  filters: {},
  setFilters: () => {},
  defaultModuleParameters: {},
  isLiveUpdating: false,
  setIsLiveUpdating: () => {},
  currentLoadingChipStatus: null,
  setCurrentLoadingChipStatus: () => {},
});

export const useWidgetContext = () => useContext(WidgetContext);

type WidgetProps = {
  widget: TWidget;
  hasEqualHeightColumns?: boolean;
  // KPI-specific passthrough props:
  dateRange?: string[];
  showFraction?: boolean;
  showTrend?: boolean;
  sx?: SxProps;
};

const Widget = ({
  widget,
  hasEqualHeightColumns,
  dateRange,
  showFraction = false,
  showTrend = true,
  sx,
}: WidgetProps) => {
  const dataProvider = useDataProvider();
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const isCustomDashboard = useIsCustomDashboard();
  const {
    assetUUID,
    filters: dashboardFilters,
    isDashboardMasked,
    hasProDataInsights,
  } = useDashboardContext();
  const { ref: viewportRef, inViewport } = useInViewport();

  const isKpi = widget?.display?.view === 'kpi';
  const isRss = rssDisplayTypes.includes(
    widget?.display?.view as 'listcard' | 'imagecard',
  );
  const isIndependentSystemWidget = getIsIndependentSystemWidget(widget.id);
  const shouldWidgetBeFetched = ![independentSystemWidgetIds.help].includes(widget.id);

  const { type: chartType, config } = useMemo(() => getChartConfig(widget), [widget]);
  const { type, refresh, isLive } = useMemo(() => getRefreshIndicator(widget), [widget]);
  const app = getWidgetAppValue(widget);
  const memoedPathname = useMemo(() => location?.pathname, [location?.pathname]);

  // CMSADMIN-1425: Check if the widget is masked.
  const isMasked = useMemo(
    () => !!widget?.advanced_required && !hasProDataInsights,
    [widget, hasProDataInsights],
  );

  const [isLiveEnabled, setIsLiveEnabled] = useLocalStorage<boolean>({
    key: isKpi ? `widget-isLiveEnabled-${widget.id}` : `widget-isLive-${widget.id}`,
    defaultValue: false,
  });
  const [currentLoadingChipStatus, setCurrentLoadingChipStatus] =
    useState<TLoadingStatusPill>(null);

  // Pagination state (for table widgets).
  const initWidgetFiltersFromDynaFields = useMemo(
    () =>
      getInitDashboardOrWidgetFiltersFromModuleDynaFields(
        normalizeModuleParameters(widget),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [widget.id],
  );
  const initWidgetFiltersFromWidgetParameters = useMemo(
    () => getInitDashboardOrWidgetFiltersFromWidgetParameters(widget),
    [widget],
  );

  // Only use initWidgetFiltersFromWidgetParameters (widget.parameters) if it has values
  // --used for widget filters (useWidgetFilters/WidgetAppliedFilters).
  // Do ***NOT*** use initWidgetFiltersFromDynaFields->api requires nulls/undefineds (as defaults)
  // to be passed for filters that are not set for default query parameters.
  const [widgetFilters, setWidgetFilters] = useState<Partial<TWidgetFilters>>(
    initWidgetFiltersFromWidgetParameters,
  );
  const [pageSize, setPageSize] = useState(
    initWidgetFiltersFromWidgetParameters?.pageSize ??
      initWidgetFiltersFromDynaFields?.pageSize ??
      10,
  );
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasFetched, setHasFetched] = useState(false);

  const supportsPagination = useMemo(() => chartType === 'table', [chartType]);

  const mergedFilters = useMemo(
    () => ({
      ...dashboardFilters,
      ...widgetFilters,
      // (Custom Dashboards) Keep hardset widget parameters immutable in query payload.
      // Even if widgetFilters state is updated, hardset values always win.
      // Check isCustomDashboard in case api returns public dashboard widgets with hard-set widget.parameters.
      ...(isCustomDashboard ? initWidgetFiltersFromWidgetParameters : {}),
    }),
    [
      dashboardFilters,
      initWidgetFiltersFromWidgetParameters,
      isCustomDashboard,
      widgetFilters,
    ],
  );

  // For table widgets with pagination, always enable the query (when basic conditions are met)
  // For other widgets, use the standard isLiveEnabled logic
  const isEnabled = useMemo(() => {
    if (supportsPagination) {
      // For paginated tables, enable as long as basic conditions are met
      // (not masked, in viewport, should be fetched) to support backend-driven pagination
      return !isMasked && inViewport && shouldWidgetBeFetched && !isDashboardMasked;
    }
    return getIsWidgetFetchEnabled({
      isMasked,
      inViewport,
      shouldWidgetBeFetched,
      isDashboardMasked,
      isLiveEnabled,
      isLive,
      hasFetched,
      id: widget.id,
    });
  }, [
    supportsPagination,
    isMasked,
    inViewport,
    shouldWidgetBeFetched,
    isDashboardMasked,
    isLiveEnabled,
    isLive,
    hasFetched,
    widget.id,
  ]);

  // For table widgets, only use refresh interval when live mode is enabled
  // Otherwise set to Infinity to prevent automatic refetching while still allowing manual pagination.
  const effectiveRefreshInterval = useMemo(() => {
    if (supportsPagination && (!isLive || !isLiveEnabled)) {
      return false; // Disable automatic refetching
    }
    return refresh;
  }, [supportsPagination, isLive, isLiveEnabled, refresh]);

  const effectiveStaleTime = useMemo(() => {
    if (supportsPagination && (!isLive || !isLiveEnabled)) {
      return Infinity; // Keep data fresh indefinitely
    }
    return refresh;
  }, [supportsPagination, isLive, isLiveEnabled, refresh]);

  // Single unified useQuery for all widget types.
  const { data, isLoading, isFetching, isError, isSuccess } = useQuery({
    queryKey: [
      'getWidgetDataById',
      widget.id,
      app,
      assetUUID,
      page,
      pageSize,
      mergedFilters,
      memoedPathname,
    ],
    queryFn: async () =>
      await dataProvider.getWidgetDataById({
        id: widget.id,
        query: {
          assetUUID,
          ...normalizeDashboardFiltersQuery(mergedFilters, widget),
          ...(supportsPagination && { page, pageSize }),
        },
      }),
    enabled: isEnabled,
    staleTime: effectiveStaleTime,
    gcTime: STALE_TIME,
    refetchInterval: effectiveRefreshInterval,
    refetchOnMount: true,
    placeholderData: (previousData: any) => previousData,
    retry: (_, error: AxiosError) => (error?.status ?? 500) < 400,
  });

  // Mark data as fetched at least once.
  useEffect(() => {
    if (!isLoading && !isFetching && !isError && data) setHasFetched(true);
  }, [data, isLoading, isFetching, isError]);

  // Sync loading chip status with fetch state.
  useEffect(() => {
    if (!isLiveEnabled) return;
    if (isError) {
      setCurrentLoadingChipStatus('error');
    } else if (!isLoading && !isFetching && data) {
      setCurrentLoadingChipStatus('live');
    }
  }, [data, isError, isLoading, isFetching, isLiveEnabled]);

  useEffect(() => {
    if (!isLiveEnabled) return;
    if (isFetching) setCurrentLoadingChipStatus('updating');
  }, [isFetching, isLiveEnabled]);

  // Update total for pagination.
  useEffect(() => {
    if (supportsPagination) setTotal(Number(data?.total ?? 0));
  }, [supportsPagination, data?.total]);

  const handlePaginationChange = (newPage: number, newPageSize?: number) => {
    setPage(newPage);
    if (newPageSize !== undefined) setPageSize(newPageSize);
  };

  const paginationContext = useMemo(
    () =>
      supportsPagination
        ? { page, pageSize, total, onChange: handlePaginationChange }
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [supportsPagination, page, pageSize, total],
  );

  // Masked/real widget+data for standard and RSS widgets.
  const chartWidgetAndData = useMemo(() => {
    if (chartType === null) return { widget, data };
    if (isMasked || isDashboardMasked) {
      const { widget: mockWidget, data: mockData } = getMaskedWidgetData({
        chartType,
        title: widget?.title,
        id: widget?.id,
      });
      return { widget: mockWidget as any as TWidget, data: mockData };
    }
    return { widget, data };
  }, [chartType, data, isDashboardMasked, isMasked, widget]);

  // Masked/real widget+data for KPI widgets.
  const kpiWidgetAndData = useMemo(() => {
    if (!isKpi) return { widget, data };
    if (isMasked || isDashboardMasked) {
      const { widget: mockWidget, data: mockData } = getMaskedWidgetData({
        chartType: 'kpi',
        title: widget?.title,
        id: widget?.id,
      });
      mockData.results = mockData.results.map(() => ({
        meter_stops: Math.floor(Math.random() * 10000),
        prev_meter_stops: Math.floor(Math.random() * 10000),
      }));
      return { widget: mockWidget as any as TWidget, data: mockData };
    }
    return { widget, data };
  }, [data, isDashboardMasked, isMasked, isKpi, widget]);

  // Parse KPI data.
  const kpi = useMemo(() => {
    if (!isKpi) return undefined;
    return parseKpiData({
      widget: kpiWidgetAndData.widget,
      data: kpiWidgetAndData.data,
      showFraction: showFraction ?? false,
      isSuccess: isMasked || isDashboardMasked ? true : isSuccess,
      isMobileOrTablet,
    });
  }, [
    kpiWidgetAndData,
    showFraction,
    isMasked,
    isDashboardMasked,
    isSuccess,
    isMobileOrTablet,
    isKpi,
  ]);

  // Shared context value for chart sub-components inside WidgetCard.
  const contextValue = useMemo(
    () => ({
      widget: chartWidgetAndData.widget,
      data: chartWidgetAndData.data,
      isLoading,
      isFetching,
      isError,
      pagination: paginationContext,
      isMasked,
      filters: widgetFilters,
      setFilters: setWidgetFilters,
      chartType,
      config,
      isLiveUpdating: isLiveEnabled,
      setIsLiveUpdating: setIsLiveEnabled,
      currentLoadingChipStatus,
      setCurrentLoadingChipStatus,
      defaultModuleParameters: initWidgetFiltersFromDynaFields,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      chartWidgetAndData,
      isLoading,
      isFetching,
      isError,
      paginationContext,
      isMasked,
      widgetFilters,
      setWidgetFilters,
      chartType,
      config,
      isLiveEnabled,
      currentLoadingChipStatus,
      initWidgetFiltersFromDynaFields,
    ],
  );

  // CMSADMIN-2687: hide the subscription purchase event widgets temporarily (backend deps needed).
  if (getIsSubscriptionPurchaseEventsWidget(widget.id)) {
    return null;
  }

  // KPI widget.
  if (isKpi) {
    return (
      <WidgetContext.Provider value={contextValue}>
        <KPI
          currentLoadingChipStatus={currentLoadingChipStatus}
          dateRange={dateRange}
          hasEqualHeightColumns={hasEqualHeightColumns}
          isError={isError}
          isLive={isLive}
          isLiveUpdating={isLiveEnabled}
          isLoading={isLoading}
          isMasked={isMasked}
          kpi={kpi}
          refresh={refresh}
          setCurrentLoadingChipStatus={setCurrentLoadingChipStatus}
          setIsLiveUpdating={setIsLiveEnabled}
          showFraction={showFraction}
          showTrend={showTrend}
          sx={sx}
          type={type}
          viewportRef={viewportRef}
          widget={kpiWidgetAndData.widget}
        />
      </WidgetContext.Provider>
    );
  }

  // RSS feed/layout widget.
  if (isRss) {
    return (
      <WidgetContext.Provider value={contextValue}>
        <RssLayout
          widget={widget}
          data={data}
          isLoading={isLoading}
          isError={isError}
          viewportRef={viewportRef}
        />
      </WidgetContext.Provider>
    );
  }

  // Standard widget.
  return (
    <WidgetContext.Provider value={contextValue}>
      <WidgetCard
        viewportRef={viewportRef}
        isLiveEnabled={isLiveEnabled}
        setIsLiveEnabled={setIsLiveEnabled}
        currentLoadingChipStatus={currentLoadingChipStatus}
        setCurrentLoadingChipStatus={setCurrentLoadingChipStatus}
        isIndependentSystemWidget={isIndependentSystemWidget}
      />
    </WidgetContext.Provider>
  );
};

export default Widget;
